import { useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  exchangeCodeAsync,
  makeRedirectUri,
  useAutoDiscovery,
} from 'expo-auth-session';
import { env } from '@/lib/env';
import { logger } from '@/lib/logger';
import { persistentStorage } from '@/lib/storage';
import { authVar } from '@/lib/apollo/auth';
import { AuthTokens } from '@/lib/types/auth';
import { useLazyQuery } from '@apollo/client/react';
import { CurrentUserDocument } from '@/features/graphql/generated/graphql';

export default function AuthCallbackScreen() {
  const { code, state, error, error_description } = useLocalSearchParams<{
    code?: string;
    state?: string;
    error?: string;
    error_description?: string;
  }>();
  const router = useRouter();
  const discovery = useAutoDiscovery(
    env.zitadelAuthorityUrl.toString().replace(/\/$/, ''),
  );
  const redirectUri =
    Platform.OS === 'web'
      ? env.zitadelCallbackUri.toString()
      : makeRedirectUri({ scheme: 'fytness', path: 'auth/callback' });
  const [fetchUser] = useLazyQuery(CurrentUserDocument);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  logger.debug('[Callback] Callback screen mounted', {
    platform: Platform.OS,
    hasCode: !!code,
    hasError: !!error,
    redirectUri,
  });

  useEffect(() => {
    logger.debug('[Callback] useEffect triggered', {
      code,
      error,
      hasDiscovery: !!discovery,
    });

    // Handle OAuth errors
    if (error) {
      logger.error('[Callback] OAuth error:', error, error_description);
      setErrorMessage(error_description || error || 'Authentication error');
      setTimeout(() => {
        router.replace('/(auth)');
      }, 3000);
      return;
    }

    // Wait for code and discovery
    if (!code) {
      logger.warn('[Callback] No code received');
      return;
    }

    if (!discovery) {
      logger.debug('[Callback] Waiting for discovery to complete...');
      return;
    }

    // Exchange code for tokens
    logger.debug('[Callback] Starting token exchange...');
    exchangeCodeAsync(
      {
        clientId: env.zitadelClientId,
        code,
        redirectUri,
      },
      discovery,
    )
      .then(async (tokens) => {
        logger.debug('[Callback] Token exchange successful');
        const authTokens = tokens as AuthTokens;
        await persistentStorage.set('auth', authTokens);
        authVar(authTokens);
        logger.debug('[Callback] Fetching user data...');
        await fetchUser();
        logger.debug('[Callback] Navigating to private area...');
        router.replace('/(private)');
      })
      .catch((e) => {
        logger.error('[Callback] Token exchange failed:', e);
        setErrorMessage('Authentication failed. Please try again.');
        setTimeout(() => {
          router.replace('/(auth)');
        }, 3000);
      });
  }, [
    code,
    state,
    error,
    error_description,
    discovery,
    redirectUri,
    fetchUser,
    router,
  ]);

  if (errorMessage) {
    return (
      <View className="flex-1 items-center justify-center p-4 bg-background">
        <Text className="text-destructive text-center mb-4">
          {errorMessage}
        </Text>
        <Text className="text-muted-foreground text-center">
          Redirecting to login...
        </Text>
      </View>
    );
  }

  return (
    <View className="flex-1 items-center justify-center bg-background">
      <ActivityIndicator size="large" />
      <Text className="text-foreground mt-4">Logging in...</Text>
    </View>
  );
}
